import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Activity, User, UserContextType, UserTab } from "../components/types";
import { useTranslation } from 'react-i18next';

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<UserTab>("Hostages");

  const fetchUser = async () => {
    try {
      const response = await fetch(`/api/user`, {
        credentials: 'include', // Include cookies in the request
      });
      if (response.ok) {
        const fetchedUser = await response.json() as User;      
        setUser(fetchedUser);
        if (fetchedUser?.language) {
          await i18n.changeLanguage(fetchedUser?.language);
          setHtmlDirection(fetchedUser.language);
        }

        // fast fix to sort the list at the beginning
        if (fetchedUser?.registered_activities?.length > 0) {
          updateRegisteredActivities(fetchedUser.registered_activities);
        }
        return fetchedUser;
      } else {
        setUser(null); // User is not authenticated
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUser(null);
    }
  };

  const setHtmlDirection = (lang: string) => {
    const html = document.documentElement;
    if (lang === 'he') {
      html.lang = 'he';
      html.dir = 'rtl';
    } else {
      html.lang = 'en';
      html.dir = 'ltr';
    }
  };
  
  const checkAuth = async () => {
    await fetchUser();
    setLoading(false);
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const updateUserLanguage = (lang: string) => {
    const updatedUser = user;
    if (updatedUser) {
      updatedUser.language = lang;
      setUser(updatedUser);
    }
  };

  // New method to update registered activities
  const updateRegisteredActivities = (activities: Activity[]) => {
    if (!Array.isArray(activities)) {
      console.error('Expected an array of activities, but got:', activities);
      return; 
  }
    //sort by date from closest to farthest
    const sortedActivities = activities.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    setUser(prevUser => (prevUser ? { ...prevUser, registered_activities: sortedActivities } : null));
  };

  useEffect(() => {
    checkAuth();
  }, []);

  if (loading) {
    return <div>Loading...</div>; // You can replace this with a proper loading spinner
  }

  return (
    <UserContext.Provider value={{ user, updateUser, fetchUser, updateRegisteredActivities, selectedTab, setSelectedTab, updateUserLanguage }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
